import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    getAreaListThunk,
    getCatalogExportThunk,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getMotivationInfoThunk,
    getOrderClosedCheckoutListThunk,
    getOrderOpenedCheckoutListThunk,
    getSchedulerMyRoutesThunk,
    getTaskListsThunk,
    getUserMeThunk,
    getUserTeamGetByManagerThunk,
    getWorkCheckThunk,
    getWorkGetScheduleThunk,
    postWorkStartThunk,
} from "tm-shared/api";
import { UserRole, WorkStatus } from "tm-shared/types";
import { warmupImages } from "tm-shared/utils";

import { ProgressLoader } from "@/app/ProgressLoader";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { QueueTaskStatus, useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import useMercure from "@/hooks/useMercure";
import { opfsQueueStorage } from "@/Services/OPFSQueueStorage";
import { fullReset } from "@/store/actions";
import { updateStatus } from "@/store/slices/auth/auth.slice";
import {
    bulkAddRequestQueueItems,
    getKpiGetMarketProgressesThunk,
    getPlansByMarketIdThunk,
    setIsInitialDataLoading,
    setLastOfflineUpdate,
    updateOfflineStatus,
} from "@/store/slices/offline";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/sv";
import { setInitFailedRequests } from "@/store/slices/ui";
import { setLoggedInAt } from "@/store/slices/user";
import { loadConfigFromServer, loadProgressFromServer } from "@/store/thunks";
import { getFileUrl, getKpiTargetDate } from "@/utils";

const COOKIE_CONFIG = {
    name: "FILE_ACCESS",
    value: "0184be96-7e48-4a20-bc4d-5bccb68a91c3",
    options: { domain: ".trademaster.in.ua", path: "/", secure: true },
} as const;

const getFormattedDates = () => ({
    today: dayjs().format("YYYY-MM-DD"),
    yesterday: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
});

export const AppInitializer = () => {
    const { queue: startDayQueue, progress: queueProgress } =
        useAsyncTaskQueue();

    const [startDayLoading, setStartDayLoading] = useState(false);

    const { isAuthenticated } = useAppSelector((s) => s.auth);
    const { id, role, loggedInAt } = useAppSelector((s) => s.user);
    const { lastOfflineUpdate, offlineMode } = useAppSelector((s) => s.offline);
    const { workStatus: storeWorkStatus } = useAppSelector((s) => s.work);

    const dispatch = useAppDispatch();
    const isInitializing = useRef(false);

    useMercure(id, storeWorkStatus, dispatch);

    const setCookie = useCallback(() => {
        Cookies.set(
            COOKIE_CONFIG.name,
            COOKIE_CONFIG.value,
            COOKIE_CONFIG.options,
        );
    }, []);

    const fetchTaOfflineData = useCallback(
        async ({
            userId,
            workStatus,
            routeId,
        }: {
            userId: string;
            routeId: string;
            workStatus: string;
        }) => {
            const commonKpiParams = {
                employeeId: userId,
                targetDate: getKpiTargetDate(),
            };

            startDayQueue.add(
                "Завантаження зарплати",
                () =>
                    dispatch(
                        getKpiGetSalaryToPayThunk(commonKpiParams),
                    ).unwrap(),
                { type: "getKpiGetSalaryToPayThunk" },
            );

            startDayQueue.add(
                "Завантаження прогресу KPI",
                () =>
                    dispatch(getKpiGetProgressThunk(commonKpiParams)).unwrap(),
                { type: "getKpiGetProgressThunk" },
            );

            startDayQueue.add(
                "Завантаження інформації про мотивацію",
                () =>
                    dispatch(
                        getMotivationInfoThunk({ employeeId: userId }),
                    ).unwrap(),
                { type: "getMotivationInfoWorkThunk" },
            );

            if (workStatus === WorkStatus.StartWork) {
                startDayQueue.add("Завантаження розкладу роботи", async () => {
                    const { result } = await dispatch(
                        getWorkGetScheduleThunk({ userId }),
                    ).unwrap();

                    const marketIds = Object.keys(result?.markets ?? {});

                    if (marketIds.length) {
                        startDayQueue.add(
                            "Завантаження інформації про задачі",
                            () =>
                                dispatch(
                                    getTaskListsThunk({ targetIds: marketIds }),
                                ).unwrap(),
                        );

                        startDayQueue.add(
                            "Завантаження інформації про плани на ТТ",
                            async () => {
                                await dispatch(
                                    getPlansByMarketIdThunk({
                                        marketIds,
                                        routeId,
                                    }),
                                );
                                startDayQueue.add(
                                    "Завантаження інформації про факти на ТТ",
                                    () =>
                                        dispatch(
                                            getKpiGetMarketProgressesThunk({
                                                marketIds,
                                            }),
                                        ),
                                );
                            },
                        );
                    }
                });
            }
        },
        [dispatch, startDayQueue],
    );

    const fetchSvOfflineData = useCallback(
        async ({
            userId,
            workStatus,
        }: {
            userId: string;
            workStatus: string;
        }) => {
            const commonKpiParams = {
                employeeId: userId,
                targetDate: getKpiTargetDate(),
            };

            if (workStatus !== WorkStatus.StartWork) {
                startDayQueue.add("Початок робочого дня", () =>
                    dispatch(postWorkStartThunk({ userId })).unwrap(),
                );
            }

            startDayQueue.add("Завантаження інформації про команду", () =>
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: userId }),
                ).unwrap(),
            );

            startDayQueue.add(
                "Завантаження інформації про маршрути команди",
                () =>
                    dispatch(
                        getSchedulerMyRoutesThunk({ ownerId: userId }),
                    ).unwrap(),
            );

            startDayQueue.add("Завантаження зарплати", () =>
                dispatch(getKpiGetSalaryToPaySvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження прогресу KPI", () =>
                dispatch(getKpiGetProgressSvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження інформації про мотивацію", () =>
                dispatch(
                    getMotivationInfoThunk({ employeeId: userId }),
                ).unwrap(),
            );
        },
        [dispatch, startDayQueue],
    );

    // отримання даних UserRole.Md
    const fetchMdOfflineData = useCallback(
        async ({
            userId,
            workStatus,
        }: {
            userId: string;
            workStatus: string;
        }) => {
            if (workStatus === WorkStatus.StartWork) {
                startDayQueue.add("Завантаження розкладу роботи", async () => {
                    const { result } = await dispatch(
                        getWorkGetScheduleThunk({ userId }),
                    ).unwrap();

                    const marketIds = Object.keys(result?.markets ?? {});

                    if (marketIds.length) {
                        startDayQueue.addAndRun(
                            "Завантаження інформації про задачі",
                            () =>
                                dispatch(
                                    getTaskListsThunk({ targetIds: marketIds }),
                                ).unwrap(),
                        );
                    }
                });
            }
        },
        [dispatch, startDayQueue],
    );

    const fetchSvmOfflineData = useCallback(
        async ({
            userId,
            workStatus,
        }: {
            userId: string;
            workStatus: string;
        }) => {
            if (workStatus !== WorkStatus.StartWork) {
                startDayQueue.add("Початок робочого дня", () =>
                    dispatch(postWorkStartThunk({ userId })).unwrap(),
                );
            }

            startDayQueue.add("Завантаження інформації про команду", () =>
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: userId }),
                ).unwrap(),
            );

            startDayQueue.add(
                "Завантаження інформації про маршрути команди",
                () =>
                    dispatch(
                        getSchedulerMyRoutesThunk({ ownerId: userId }),
                    ).unwrap(),
            );
        },
        [dispatch, startDayQueue],
    );

    const runQueue = useCallback(async () => {
        try {
            await startDayQueue.runParallel();

            const failedTypes = startDayQueue.progress.tasks.reduce(
                (acc, task) => {
                    if (task.status === QueueTaskStatus.Completed) return acc;

                    const t = task?.metadata?.type;
                    if (!t) return acc;
                    return acc.includes(t) ? acc : [...acc, t];
                },
                [],
            );

            dispatch(setInitFailedRequests(failedTypes));
        } catch (error) {
            console.error("Init error:", error);
        } finally {
            dispatch(setLastOfflineUpdate(new Date().toISOString()));
            isInitializing.current = false;

            setTimeout(() => {
                setStartDayLoading(false);
                startDayQueue.clear();

                dispatch(setIsInitialDataLoading(false));
            }, 2000);
        }
    }, [dispatch, startDayQueue]);

    useEffect(() => {
        if (!isAuthenticated || isInitializing.current || offlineMode) return;

        if (
            lastOfflineUpdate &&
            dayjs(lastOfflineUpdate).isSame(dayjs(), "day")
        ) {
            return;
        }

        const init = async () => {
            let roleSlug: string;
            let userId: string;
            let workStatus: string;
            let routeId: string;

            setStartDayLoading(true);
            isInitializing.current = true;

            try {
                const { result: resultUserMe } = await startDayQueue.addAndRun(
                    "Отримання даних користувача",
                    () => dispatch(getUserMeThunk()).unwrap(),
                );

                roleSlug = resultUserMe.authorizedRole.slug;
                userId = resultUserMe.userId;
                routeId = resultUserMe?.team?.route?.uuid;

                const { result: resultWorkCheck } =
                    await startDayQueue.addAndRun(
                        "Перевірка статусу роботи",
                        () => dispatch(getWorkCheckThunk({ userId })).unwrap(),
                    );

                workStatus = String(resultWorkCheck);
            } catch {
                setStartDayLoading(false);
                isInitializing.current = false;
                startDayQueue.clear();

                return;
            }

            startDayQueue.add("Синхронізація прогресу", async () => {
                dispatch(loadConfigFromServer({ userId, roleSlug }));
                await dispatch(
                    loadProgressFromServer({ userId, roleSlug }),
                ).unwrap();

                // restore unfinished queue items from previous days
                const groups = await opfsQueueStorage.loadPendingFromPastDays(
                    userId,
                    roleSlug,
                );
                const items = groups.flatMap((g) => g.items);
                if (items.length > 0) {
                    dispatch(bulkAddRequestQueueItems(items));
                }
            });

            startDayQueue.add(
                "Отримання доступних регіонів",
                () => dispatch(getAreaListThunk()).unwrap(),
                { type: "getAreaListThunk" },
            );

            startDayQueue.add(
                "Завантаження каталогу",
                async () => {
                    const { result } = await dispatch(
                        getCatalogExportThunk(),
                    ).unwrap();

                    const imageUrls = result.products
                        .map((p) => p.params.image.value.map(getFileUrl))
                        .flat();

                    warmupImages(imageUrls);
                },
                { type: "getCatalogExportThunk" },
            );

            switch (roleSlug) {
                case UserRole.Md:
                case UserRole.Kam: {
                    await fetchMdOfflineData({ userId, workStatus });
                    break;
                }

                case UserRole.Sv: {
                    await fetchSvOfflineData({ userId, workStatus });
                    break;
                }

                case UserRole.Svm: {
                    await fetchSvmOfflineData({ userId, workStatus });
                    break;
                }

                case UserRole.Ta: {
                    await fetchTaOfflineData({ userId, workStatus, routeId });

                    break;
                }

                default:
                    break;
            }

            await runQueue();
        };

        setCookie();

        void init();
    }, [
        isAuthenticated,
        setCookie,
        dispatch,
        lastOfflineUpdate,
        runQueue,
        startDayQueue,
        fetchMdOfflineData,
        fetchSvOfflineData,
        fetchSvmOfflineData,
        fetchTaOfflineData,
        offlineMode,
    ]);

    useEffect(() => {
        if (
            role !== UserRole.Ta ||
            !id ||
            storeWorkStatus !== WorkStatus.StartWork ||
            offlineMode
        ) {
            return;
        }

        const { today } = getFormattedDates();

        dispatch(getOrderOpenedCheckoutListThunk({ agentId: id }));
        dispatch(
            getOrderClosedCheckoutListThunk({
                agentId: id,
                targetDate: today,
            }),
        );
    }, [dispatch, id, role, storeWorkStatus, offlineMode]);

    useEffect(() => {
        const updateNetworkStatus = () =>
            dispatch(updateStatus(navigator.onLine));
        updateNetworkStatus();
        window.addEventListener("online", updateNetworkStatus);
        window.addEventListener("offline", updateNetworkStatus);
        return () => {
            window.removeEventListener("online", updateNetworkStatus);
            window.removeEventListener("offline", updateNetworkStatus);
        };
    }, [dispatch]);

    useEffect(() => {
        const handleForceOffline = () => {
            dispatch(updateOfflineStatus(true));
        };

        window.addEventListener("force-offline", handleForceOffline);

        return () => {
            window.removeEventListener("force-offline", handleForceOffline);
        };
    }, [dispatch]);

    useEffect(() => {
        const previousUserId = localStorage.getItem("prevUserId");
        if (!id || previousUserId === id) return;

        localStorage.setItem("prevUserId", id);
        dispatch(fullReset());
    }, [dispatch, id]);

    useEffect(() => {
        const now = dayjs();
        const isValid = !!loggedInAt && dayjs(loggedInAt).isValid();
        const isToday = isValid && dayjs(loggedInAt).isSame(now, "day");

        if (isValid && !isToday) {
            dispatch(setLoggedInAt(now.toISOString()));
            dispatch(fullReset());

            return;
        }

        dispatch(setLoggedInAt(now.toISOString()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    return (
        <ProgressLoader
            open={startDayLoading || queueProgress.isRunning}
            tasks={queueProgress.tasks}
        />
    );
};
